// Parent Homework child exercise screen (SC-H2, design §2.4 + §3 plain).
// Rendered inside the mounted surface (no re(), no fetch). Tile taps open this screen.

import { exerciseSlotsOn, isExerciseScheduledOn } from '../data.js';
import { pickMechanic } from './mechanics/index.js';
import { openParentSheet } from './parent-sheet.js';
import { linkifyInto, firstYoutube, splitForTiles } from './linkify.js';
import { makeYoutubeTile } from './youtube-tile.js';
import { showCelebration } from './celebrate.js';
import {
  _fmtLocal, _slotItemsOn, _slotGroups, _slotBucket, _slotName, _dose, _dotColor, _firstName, _tintFor, _artFor, _esc,
} from './today.js';

const BADGES = ['🌟', '🎉', '🏆', '🦄', '🚀', '🐬'];
// Group-done celebration keys, by slot bucket. Explicit literal map (not `'hw5_'+bucket+'_done'`)
// so the H5 dead-key grep sweep can see them referenced. SC-H4.1.
const GROUP_DONE_KEY = { morning: 'hw5_morning_done', afternoon: 'hw5_afternoon_done', evening: 'hw5_evening_done', anytime: 'hw5_anytime_done' };

const T = (k, p) => window.HUD?.T?.(k, p) || k;

const VIDEO_RE = /\.(mp4|mov|webm|m4v|avi|mkv)$/i;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif)$/i;

// First-open-per-day tracking for the "Watch first" pulse (once per exercise per day).
const _openedToday = new Set();

// Split an exercise's attachments into videos / images / others (PDF, docx, …). §1f classifier.
function _classifyAttachments(ex) {
  const names = ex.attached_file_names || [], paths = ex.attached_file_paths || [], urls = ex.attached_file_urls || [];
  const n = Math.max(names.length, paths.length, urls.length);
  const videos = [], images = [], others = [];
  for (let i = 0; i < n; i++) {
    const item = { name: names[i] || '', path: paths[i] || null, url: urls[i] || null };
    if (VIDEO_RE.test(item.name)) videos.push(item);
    else if (IMAGE_RE.test(item.name)) images.push(item);
    else others.push(item);
  }
  return { videos, images, others };
}

function _compFor(compMap, exId, dStr, slot) { return compMap[exId + ':' + dStr + ':' + (slot || '')]; }

const _statusChip = status => status === 'done' ? T('hw3_st_done') + ' ✓'
  : status === 'skipped' ? T('hw3_st_skipped')
  : status === 'cant_do' ? T('hw3_st_cant') : '';

// Sign a video for inline playback (3600s, per D11). Bypasses the surface thumbnail cache (900s).
function _signVideo(path) {
  const H = window.HUD || {};
  if (!path) return Promise.resolve(null);
  try {
    if (typeof H._signFilePool === 'function') return H._signFilePool(path, { ttl: 3600 }).catch(() => null);
    if (H.SB?.signFile) return H.SB.signFile(path, { ttl: 3600 }).catch(() => null);
  } catch (_) {}
  return Promise.resolve(null);
}

// Open a non-media attachment (PDF/docx) in a new tab via a signed URL (iOS-safe).
function _openFile(path) {
  const H = window.HUD || {};
  const tab = window.open('about:blank', '_blank');
  const p = (typeof H._signFilePool === 'function') ? H._signFilePool(path) : (H.SB?.signFile ? H.SB.signFile(path) : Promise.resolve(null));
  p.then(url => { if (url && tab) tab.location = url; else if (tab) tab.close(); }, () => { if (tab) tab.close(); });
}

// ── F-104: the homework note (hw.description) + the current exercise's instruction, for the parent ──
// Intro card: shown once per homework per device (the first time its exercise screen opens, when the specialist left a note).
// The ⓘ in the flow header opens the same two texts in a bottom sheet on every step. Plain text only (links are F-105).
const INTRO_KEY = id => 'hud_hwp_intro_' + id;
const _introSeen = id => { try { return localStorage.getItem(INTRO_KEY(id)) === '1'; } catch (_) { return false; } };
const _introMark = id => { try { localStorage.setItem(INTRO_KEY(id), '1'); } catch (_) {} };
const _note = hw => String((hw && hw.description) || '').trim();
const _instr = ex => String((ex && ex.instructions) || '').trim();

function _renderIntro(wrap, hw, ctx, onGo) {
  const top = document.createElement('div'); top.className = 'hwp-ex-top';
  top.innerHTML = `<button type="button" class="hwp-back">${ctx.origin === 'board' ? '‹ ' + _esc(hw.title) : '‹ ' + _esc(T('hw5_today'))}</button>`;
  top.querySelector('.hwp-back').onclick = () => ctx.onBack();
  wrap.appendChild(top);
  const card = document.createElement('div'); card.className = 'hwp-intro';
  const initial = (Array.from(String(hw.specialist_name || '').trim())[0] || '').toUpperCase();
  card.innerHTML =
    `<div class="hwp-intro-card">` +
      `<div class="hwp-intro-from"><span class="hwp-intro-av" aria-hidden="true">${_esc(initial)}</span><span class="hwp-intro-who"><b>${_esc(hw.specialist_name || '')}</b><small>${_esc(T('hw6_intro_byline'))}</small></span></div>` +
      `<div class="hwp-intro-head">${_esc(T('hw6_intro_heading'))}</div>` +
      `<p class="hwp-intro-note" dir="auto"></p>` +
    `</div>` +
    `<button type="button" class="hwp-intro-start primary">${_esc(T('hw6_intro_start'))}</button>` +
    `<button type="button" class="hwp-intro-skip">${_esc(T('hw6_intro_skip'))}</button>`;
  const _introNote = card.querySelector('.hwp-intro-note');
  const _introYt = firstYoutube(linkifyInto(_introNote, _note(hw)));
  if (_introYt) _introNote.after(makeYoutubeTile(_introYt.id));   // the specialist's own words as text nodes, http(s) links as anchors (F-105); never HTML
  const go = () => { _introMark(hw.id); onGo(); };
  card.querySelector('.hwp-intro-start').onclick = go;
  card.querySelector('.hwp-intro-skip').onclick = go;
  wrap.appendChild(card);
}

// The round info button (44px target, the mock's 30px circle inside): opens the sheet for this homework (+ this exercise, or note only when ex is null).
export function makeInfoButton(hw, ex) {
  const info = document.createElement('button'); info.type = 'button'; info.className = 'hwp-info'; info.setAttribute('aria-label', T('hw5_instructions'));
  info.innerHTML = '<span class="hwp-info-dot" aria-hidden="true">i</span>';
  info.onclick = () => _openInfoSheet(hw, ex, info);
  return info;
}
export const hasHwNote = hw => !!_note(hw);

// Bottom sheet: Esc, the backdrop and the Close button all close it; nothing behind it is touched, so closing returns exactly in place.
export function _openInfoSheet(hw, ex, opener) {
  const bg = document.createElement('div'); bg.className = 'hwp-sheet-bg';
  const sheet = document.createElement('div'); sheet.className = 'hwp-sheet hwp-info-sheet'; sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true');
  const note = _note(hw), instr = _instr(ex);
  sheet.innerHTML =
    `<div class="hwp-sheet-grab"></div>` +
    `<h3 class="hwp-info-h">${_esc(T('hw6_info_title', { name: _firstName(hw.specialist_name) }))}</h3>` +
    (note ? `<p class="hwp-info-note" dir="auto"></p>` : '') +
    (instr ? `<div class="hwp-info-ex"><b>${_esc(ex.title || T('hw3_exercise'))}</b><p class="hwp-info-instr" dir="auto"></p></div>` : '') +
    `<button type="button" class="hwp-info-close">${_esc(T('btn_close'))}</button>`;
  // F-105b: one tile per distinct YouTube video, under the text block that holds its link (the note first, then the instruction)
  let _ytShown = null;
  if (note) { const el = sheet.querySelector('.hwp-info-note'), yt = firstYoutube(linkifyInto(el, note)); if (yt) { el.after(makeYoutubeTile(yt.id)); _ytShown = yt.id; } }
  if (instr) { const el = sheet.querySelector('.hwp-info-instr'), yt = firstYoutube(linkifyInto(el, ex.instructions)); if (yt && yt.id !== _ytShown) el.after(makeYoutubeTile(yt.id)); }
  const onKey = e => { if (e.key === 'Escape') close(); };
  const close = () => {
    document.removeEventListener('keydown', onKey);
    sheet.classList.remove('on'); bg.remove(); setTimeout(() => sheet.remove(), 280);
    try { if (opener && opener.isConnected) opener.focus({ preventScroll: true }); } catch (_) {}
  };
  bg.onclick = close; sheet.querySelector('.hwp-info-close').onclick = close;
  document.addEventListener('keydown', onKey);
  document.body.appendChild(bg); document.body.appendChild(sheet);
  requestAnimationFrame(() => sheet.classList.add('on'));
}

// ── Hero variants (§2.4) ──
function _renderHero(host, ex, hw, media, firstName, ctx, instructionInHero) {
  const tint = _tintFor(ex.id);
  const hero = document.createElement('div');
  hero.className = 'hwp-hero t-' + tint;

  if (media.videos.length) {
    const v = media.videos[0];
    const key = ex.id + ':' + ctx.dateStr;
    const pulse = !_openedToday.has(key);
    _openedToday.add(key);
    const durTxt = ex.duration_seconds ? ' · ' + Math.floor(ex.duration_seconds / 60) + ':' + String(ex.duration_seconds % 60).padStart(2, '0') : '';
    hero.innerHTML =
      `<span class="hwp-hero-art" style="opacity:.55">${_artFor(ex.id)}</span>` +
      `<div class="hwp-hero-vid"><button type="button" class="hwp-hero-play" aria-label="Play">▶</button></div>` +
      `<span class="hwp-hero-tag">${_esc(T('hw5_shows_you_how', { name: firstName }))}${durTxt}</span>` +
      `<button type="button" class="hwp-hero-watch${pulse ? ' pulse' : ''}">${_esc(T('hw5_watch_first'))}</button>`;
    const play = () => {
      _signVideo(v.path || null).then(url => {
        const src = url || v.url || null;
        if (!src) return;
        hero.innerHTML = '';
        const vid = document.createElement('video');
        vid.src = src; vid.controls = true; vid.autoplay = true; vid.setAttribute('playsinline', '');
        vid.className = 'hwp-hero-video';
        hero.appendChild(vid);
      });
    };
    hero.querySelector('.hwp-hero-play').onclick = play;
    hero.querySelector('.hwp-hero-watch').onclick = play;
  } else if (media.images.length === 1) {
    // Full picture (contain) on the tint; tap → enlarged view. Reuse the one signed URL (no extra request).
    const img = media.images[0];
    hero.classList.add('hwp-hero-img');
    hero.innerHTML =
      `<img class="hwp-hero-photo" alt="">` +
      `<span class="hwp-hero-tag">${_esc(T('hw5_picture_of', { name: firstName }))}</span>`;
    const photo = hero.querySelector('.hwp-hero-photo');
    const cache = [null];
    const setSrc = url => { if (url) { cache[0] = url; photo.src = url; } };
    if (img.path) ctx.sign(img.path).then(setSrc);
    else if (img.url) setSrc(img.url);
    photo.onclick = () => { if (cache[0] || img.path || img.url) _openLightbox([img], 0, cache, ctx); };
  } else if (media.images.length >= 2) {
    _renderCarousel(hero, media.images.slice(0, 5), ctx);
  } else {
    // none → tint gradient + art + (optionally) the instruction in the hero
    hero.classList.add('hwp-hero-none');
    const art = `<span class="hwp-hero-art">${_artFor(ex.id)}</span>`;
    if (instructionInHero && (ex.instructions || '').trim()) {
      // F-105: built as DOM nodes (text + safe http(s) anchors), never an HTML string
      const p = document.createElement('p'); p.className = 'hwp-hero-instr'; p.setAttribute('dir', 'auto');
      linkifyInto(p, ex.instructions); hero.replaceChildren(p);
    } else hero.innerHTML = art;
  }
  host.appendChild(hero);
}

function _renderCarousel(hero, images, ctx) {
  hero.classList.add('hwp-hero-carousel');
  let idx = 0;
  const signed = new Array(images.length).fill(null); // per-frame signed URL cache (shared with the lightbox)
  const imgEl = document.createElement('img'); imgEl.className = 'hwp-hero-photo';
  const prev = document.createElement('button'); prev.type = 'button'; prev.className = 'hwp-carousel-arrow start'; prev.textContent = '‹';
  const next = document.createElement('button'); next.type = 'button'; next.className = 'hwp-carousel-arrow end'; next.textContent = '›';
  const dots = document.createElement('div'); dots.className = 'hwp-carousel-dots';
  images.forEach((_, i) => { const d = document.createElement('i'); if (i === 0) d.className = 'on'; dots.appendChild(d); });
  const show = i => {
    idx = (i + images.length) % images.length;
    const my = idx, im = images[my];
    // Lazy-sign: prefer the storage path (sign FRESH) — the stored attached_file_urls are signed at
    // upload time and long-expired, so they are only a last-resort fallback for path-less legacy rows.
    if (signed[my]) imgEl.src = signed[my];
    else if (im.path) ctx.sign(im.path).then(url => { if (url) { signed[my] = url; if (idx === my) imgEl.src = url; } });
    else if (im.url) { signed[my] = im.url; imgEl.src = im.url; }
    else imgEl.removeAttribute('src');
    [...dots.children].forEach((d, di) => d.className = di === idx ? 'on' : '');
  };
  prev.onclick = () => show(idx - 1);
  next.onclick = () => show(idx + 1);
  imgEl.onclick = () => _openLightbox(images, idx, signed, ctx);
  // Swipe (RTL-aware via the DOM's own direction: arrows use logical start/end).
  let sx = null;
  hero.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
  hero.addEventListener('touchend', e => {
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx; sx = null;
    if (Math.abs(dx) < 30) return;
    const rtl = getComputedStyle(hero).direction === 'rtl';
    show(idx + ((dx < 0) === rtl ? -1 : 1));
  }, { passive: true });
  hero.appendChild(imgEl); hero.appendChild(prev); hero.appendChild(next); hero.appendChild(dots);
  show(0);
}

// Enlarged image view (SC-H3.2). Full-screen dimmed overlay inside the app frame; image at contain fit.
// Reuses the caller's signed-URL cache (shared array) so opening costs zero extra requests. Carousel
// arrows work here too; a not-yet-signed frame signs once (cache-backed, dedup with the carousel).
function _openLightbox(images, startIdx, signedCache, ctx) {
  let i = ((startIdx % images.length) + images.length) % images.length;
  const multi = images.length > 1;

  const bg = document.createElement('div'); bg.className = 'hwp-lightbox-bg';
  const box = document.createElement('div'); box.className = 'hwp-lightbox';
  const img = document.createElement('img'); img.className = 'hwp-lightbox-img'; img.alt = '';
  const close = document.createElement('button'); close.type = 'button'; close.className = 'hwp-lightbox-close';
  close.textContent = '✕'; close.setAttribute('aria-label', T('btn_close'));
  box.appendChild(img); box.appendChild(close);

  let prev, next;
  if (multi) {
    prev = document.createElement('button'); prev.type = 'button'; prev.className = 'hwp-lightbox-arrow start'; prev.textContent = '‹';
    next = document.createElement('button'); next.type = 'button'; next.className = 'hwp-lightbox-arrow end'; next.textContent = '›';
    box.appendChild(prev); box.appendChild(next);
  }

  const paint = () => {
    const my = i, im = images[my];
    // Same lazy-sign precedence as the carousel: fresh-sign the path; stored url only as a fallback.
    if (signedCache[my]) img.src = signedCache[my];
    else if (im.path) ctx.sign(im.path).then(url => { if (url) { signedCache[my] = url; if (i === my) img.src = url; } });
    else if (im.url) { signedCache[my] = im.url; img.src = im.url; }
    else img.removeAttribute('src');
  };
  const go = d => { i = (i + d + images.length) % images.length; paint(); };

  const destroy = () => { bg.remove(); box.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = e => {
    if (e.key === 'Escape') destroy();
    else if (multi && e.key === 'ArrowLeft') go(-1);
    else if (multi && e.key === 'ArrowRight') go(1);
  };

  bg.onclick = destroy;                         // backdrop tap closes
  box.onclick = e => { if (e.target === box) destroy(); }; // letterbox area around the image also closes
  img.onclick = e => e.stopPropagation();        // tapping the image itself does not close
  close.onclick = e => { e.stopPropagation(); destroy(); };
  if (multi) { prev.onclick = e => { e.stopPropagation(); go(-1); }; next.onclick = e => { e.stopPropagation(); go(1); }; }
  document.addEventListener('keydown', onKey);

  document.body.appendChild(bg); document.body.appendChild(box);
  paint();
}

// ctx: { homeworks, compMap, childId, H, hwId, exId, slot, origin, dateStr, onBack, onOpenExercise, onSave, sign }
export function renderExerciseInto(wrap, ctx) {
  const { H } = ctx;
  const hw = (ctx.homeworks || []).find(h => h.id === ctx.hwId);
  const ex = hw && (hw.exercises || []).find(e => e.id === ctx.exId);
  if (!hw || !ex) { ctx.onBack(); return; }
  // F-104: the first time a homework with a specialist note opens on this device, show the intro card; either button continues to the exercise.
  if (_note(hw) && !_introSeen(hw.id)) { _renderIntro(wrap, hw, ctx, () => { wrap.innerHTML = ''; renderExerciseInto(wrap, ctx); }); return; }
  let live = null; // active mechanic controller — stopped on any navigation away (item 7: discard, no write)

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dateStr = ctx.dateStr || _fmtLocal(today);
  ctx.dateStr = dateStr;
  const firstName = _firstName(hw.specialist_name);
  const slots = exerciseSlotsOn(hw, ex, today);
  const multiSlot = slots.length > 1;
  // QA P0-3: `existing` is re-pointed at the saved row once the celebration-time save lands, so the
  // rating update / parent-note sheet / "didn't do it" all target that row (never a stale snapshot).
  let existing = _compFor(ctx.compMap, ex.id, dateStr, ctx.slot);
  let _saveInFlight = false;      // ignore a double-tap while the celebration-time save is in flight
  let _celebrationSaved = false;  // the celebration-time save landed → the sheet must not re-accumulate
  const media = _classifyAttachments(ex);
  const noHeroMedia = !media.videos.length && !media.images.length;
  // F-105c: every YouTube link in the instructions becomes a tile that takes the link's place; with tiles the text goes under the title (with the tiles in order, below any
  // uploaded video / picture), not into the hero's fixed 200px box
  const tileRuns = splitForTiles(ex.instructions || '');
  const hasTiles = tileRuns.some(r => r.type === 'yt');
  // Placement (planning + Eytan, 2026-10-21): the YouTube tiles form the media zone. With NO uploaded video or picture the decorative hero is not drawn at all and the
  // tile(s) take the top slot (one box); with an uploaded video (or picture) that hero keeps the top slot and the tile(s) stack directly beneath it. The instruction text
  // (links taken out) stays under the title.
  const tileIds = tileRuns.filter(r => r.type === 'yt').map(r => r.id);
  const textRuns = tileRuns.filter(r => r.type === 'text');
  const promoteTiles = noHeroMedia && hasTiles;
  const instructionInHero = noHeroMedia && !hasTiles;

  // Queue (SC-H4.1) = the OPEN slots of the tapped tile's SLOT GROUP (Morning / Afternoon /
  // Evening / Anytime), slot-major, for this origin (board = this homework only). Same slot-major
  // bucketing as the tiles (_slotGroups) so the walk matches the visual grouping; the current tile
  // is always kept (so replaying a done tile still pages). No cross-group rollover.
  const originHws = ctx.origin === 'board' ? [hw] : ctx.homeworks;
  const _isCur = q => q.ex.id === ex.id && q.slot === ctx.slot;
  const _groupItems = (_slotGroups(_slotItemsOn(originHws, today)).find(g => g.bucket === _slotBucket(ctx.slot)) || {}).items || [];
  const queue = _groupItems.filter(q => _isCur(q) || _compFor(ctx.compMap, q.ex.id, dateStr, q.slot)?.status !== 'done');
  const idx = queue.findIndex(_isCur);
  const curIdx = idx < 0 ? 0 : idx;

  // Back row (← Today / ← <homework>) + the ⓘ (F-104) when there is a note or an instruction to show
  const top = document.createElement('div');
  top.className = 'hwp-ex-top';
  const backLabel = ctx.origin === 'board' ? '‹ ' + _esc(hw.title) : '‹ ' + _esc(T('hw5_today'));
  top.innerHTML = `<button type="button" class="hwp-back">${backLabel}</button>`;
  top.querySelector('.hwp-back').onclick = () => { if (live) live.stop(); ctx.onBack(); };
  if (_note(hw) || _instr(ex)) {
    top.appendChild(makeInfoButton(hw, ex));
  }
  wrap.appendChild(top);

  // Pager: ‹ k / n (· slot) › — next/back within the entry queue (Today or board).
  // The "k / n" numbers are LTR-isolated so they read correctly under RTL (SC-H3 item 8).
  const prevQ = curIdx > 0 ? queue[curIdx - 1] : null;
  const nextQ = curIdx < queue.length - 1 ? queue[curIdx + 1] : null;
  const pager = document.createElement('div');
  pager.className = 'hwp-ex-pager-row';
  pager.innerHTML =
    `<button type="button" class="hwp-pager-arrow start"${prevQ ? '' : ' disabled'}>‹</button>` +
    `<span class="hwp-ex-pager"><span dir="ltr">${curIdx + 1} / ${queue.length}</span>${multiSlot ? ' · ' + _esc(_slotName(ctx.slot)) : ''}</span>` +
    `<button type="button" class="hwp-pager-arrow end"${nextQ ? '' : ' disabled'}>›</button>`;
  if (prevQ) pager.querySelector('.start').onclick = () => { if (live) live.stop(); ctx.onOpenExercise(prevQ.hw.id, prevQ.ex.id, prevQ.slot); };
  if (nextQ) pager.querySelector('.end').onclick = () => { if (live) live.stop(); ctx.onOpenExercise(nextQ.hw.id, nextQ.ex.id, nextQ.slot); };
  wrap.appendChild(pager);

  // Hero
  if (!promoteTiles) _renderHero(wrap, ex, hw, media, firstName, ctx, instructionInHero);
  if (hasTiles) {
    const yts = document.createElement('div'); yts.className = 'hwp-ex-yts' + (promoteTiles ? ' top' : '');
    for (const id of tileIds) { const t = makeYoutubeTile(id); if (t) yts.appendChild(t); }
    wrap.appendChild(yts);
  }

  // Instructions chip for non-media attachments (PDF/docx)
  if (media.others.length) {
    const chip = document.createElement('button');
    chip.type = 'button'; chip.className = 'hwp-instr-chip';
    chip.textContent = '📄 ' + T('hw5_instructions');
    chip.onclick = () => _openFile(media.others[0].path || null);
    wrap.appendChild(chip);
  }

  // Title + instruction (instruction omitted when it's already in the hero)
  const title = document.createElement('h2'); title.className = 'hwp-ex-title ff'; title.textContent = ex.title || T('hw3_exercise');
  wrap.appendChild(title);
  if (!instructionInHero && hasTiles) {
    if (textRuns.length) {
      const box = document.createElement('div'); box.className = 'hwp-ex-instrs';
      for (const r of textRuns) { const p = document.createElement('p'); p.className = 'hwp-ex-instr'; p.setAttribute('dir', 'auto'); linkifyInto(p, r.text); box.appendChild(p); }
      wrap.appendChild(box);
    }
  } else if (!instructionInHero && (ex.instructions || '').trim()) {
    const instr = document.createElement('p'); instr.className = 'hwp-ex-instr'; instr.setAttribute('dir', 'auto'); linkifyInto(instr, ex.instructions);
    wrap.appendChild(instr);
  }

  // Chips: dose · slot (if >1) · homework + dot · status (if a completion exists)
  const chips = document.createElement('div'); chips.className = 'hwp-ex-chips';
  let chipsHtml = `<span class="hwp-chip">${_esc(_dose(ex))}</span>`;
  if (multiSlot) chipsHtml += `<span class="hwp-chip">${_esc(_slotName(ctx.slot))}</span>`;
  chipsHtml += `<span class="hwp-chip from"><i class="hwp-dot" style="background:${_dotColor(hw.specialist_id)}"></i>${_esc(hw.title)}</span>`;
  if (existing) chipsHtml += `<span class="hwp-chip done-chip">${_esc(_statusChip(existing.status))}</span>`;
  chips.innerHTML = chipsHtml;
  wrap.appendChild(chips);

  // Mechanic zone — timer / counter / plain. Finish → celebration (SC-H4), NOT the sheet directly.
  const mech = document.createElement('div'); mech.className = 'hwp-mech';
  wrap.appendChild(mech);
  const mechanic = pickMechanic(ex);
  live = mechanic.render(mech, ex, {
    onFinish: (result) => _celebrate(result, true),   // a mechanic was played → face-gated, no skip
    tint: _tintFor(ex.id),
    toast: H.toast,
  });

  // Footer: "Already did it — just mark done" for timer/counter (→ celebration with the full target
  // dose and a skip link), plus "Didn't do it today" (→ sheet, direct). Plain has no mark-done.
  const foot = document.createElement('div'); foot.className = 'hwp-ex-foot';
  if (mechanic.key !== 'plain') {
    const markDone = document.createElement('button'); markDone.type = 'button'; markDone.className = 'primary';
    markDone.textContent = T('hw5_mark_done_direct');
    markDone.onclick = () => _celebrate(_fullTarget(), false);   // mark-done → celebration, not played
    foot.appendChild(markDone);
  }
  const didnt = document.createElement('button'); didnt.type = 'button'; didnt.textContent = T('hw5_didnt_do');
  didnt.onclick = () => { if (live) live.stop(); _openSheetDirect('skipped'); };
  foot.appendChild(didnt);
  wrap.appendChild(foot);

  // ── SC-H4 flow helpers ──
  const _fullTarget = () =>
    ex.reps ? { actual_value: (ex.reps || 0) * (ex.sets || 1) }
      : ex.duration_seconds ? { actual_seconds: (ex.duration_seconds || 0) * (ex.sets || 1) }
        : {};

  // Result line for the celebration (design §2.5). Accumulate → the running total (counter only).
  function _resultLine(result) {
    if (result.actual_seconds != null) {
      const s = result.actual_seconds;
      return s >= 60 ? T('hw5_result_minutes', { m: Math.round(s / 60) }) : T('hw5_result_seconds', { s });
    }
    if (result.actual_value != null) {
      if (existing?.status === 'done') return T('hw5_result_total_today', { n: (existing.actual_value || 0) + result.actual_value });
      return T('hw5_result_units', { n: result.actual_value, unit: ex.measure_unit || T('hw3_reps_label') });
    }
    return T('hw5_result_plain');
  }

  // The one 'done' write payload — accumulate-aware AND carrying forward an existing note/photo/rating
  // so re-completing a row never wipes them (only the mechanic's values + the picked rating change).
  function _donePayload(result, rating) {
    const wasDone = existing?.status === 'done';
    const av = result.actual_value != null ? (wasDone ? (existing.actual_value || 0) + result.actual_value : result.actual_value) : (wasDone ? (existing.actual_value ?? null) : null);
    const as = result.actual_seconds != null ? (wasDone ? (existing.actual_seconds || 0) + result.actual_seconds : result.actual_seconds) : (wasDone ? (existing.actual_seconds ?? null) : null);
    return {
      hwId: hw.id, exId: ex.id, slot: ctx.slot, scheduledDate: dateStr, status: 'done',
      note: existing?.note ?? null, photoPath: existing?.photo_path ?? null, mediaType: existing?.media_type ?? null,
      actualValue: av, actualSeconds: as,
      childRating: rating ?? (wasDone ? (existing.child_rating ?? null) : null),
      existingCompletionId: existing?.id || null, previousStatus: existing?.status || null,
    };
  }

  // Next open slot in the entry queue (board = this homework; Today = all), excluding the current one.
  const _nextOpen = () => queue.find(q => !(q.ex.id === ex.id && q.slot === ctx.slot)
    && _compFor(ctx.compMap, q.ex.id, dateStr, q.slot)?.status !== 'done');
  const _goNextOrBack = () => { const nx = _nextOpen(); if (nx) ctx.onOpenExercise(nx.hw.id, nx.ex.id, nx.slot); else ctx.onBack(); };

  function _celebrate(result, played) {
    if (live) live.stop();
    if (_saveInFlight) return;
    const todayAll = _slotItemsOn(ctx.homeworks, today);
    const doneTodayCount = todayAll.filter(q => _compFor(ctx.compMap, q.ex.id, dateStr, q.slot)?.status === 'done').length;
    const afterDone = doneTodayCount + (existing?.status === 'done' ? 0 : 1);
    const dayDone = todayAll.length > 0 && afterDone >= todayAll.length;
    // SC-H4.1 boundary: more open in THIS group → great_job (advance); else the whole day done →
    // "You did everything!" (day-done outranks group-done); else this group is done → group-done.
    const nx = _nextOpen();
    const titleKey = nx ? 'hw5_great_job'
      : dayDone ? 'hw5_did_everything'
        : (GROUP_DONE_KEY[_slotBucket(ctx.slot)] || 'hw5_great_job');
    const resultText = _resultLine(result);   // computed from the PRE-save snapshot (accumulate-aware)
    // QA P0-3: persist NOW — the celebration tells the parent they're done, and leaving it any way
    // (bottom nav, back, closing the app) must not lose the completion. This is the one write that
    // INSERTs (legacy v1 dual-write + specialist notification fire here, exactly once, inside
    // logExerciseCompletion). The rating / Next / parent-note sheet only UPDATE this same row
    // (status unchanged → no re-notify, no second dual-write). `base` is computed ONCE so a later
    // update never re-accumulates the mechanic values.
    const base = _donePayload(result, null);
    _saveInFlight = true;
    let saveP = ctx.onSave(base).then(res => {
      _saveInFlight = false;
      if (res && !res.error && res.row) { existing = res.row; _celebrationSaved = true; }
      return res;
    }, e => { _saveInFlight = false; console.error('❌ [hwp] celebration save:', e); return { error: true }; });
    let celCtl = null;
    celCtl = showCelebration({
      played,
      title: T(titleKey),
      resultText,
      badgeEmoji: BADGES[afterDone % BADGES.length],
      nextLabel: T(nx ? 'hw5_next_exercise' : 'hw5_back'),
      onNext: async (rating) => {
        const res = await saveP;
        if (!res || res.error) {
          // The celebration-time save failed (error already toasted) → retry once, carrying the rating.
          saveP = ctx.onSave(_donePayload(result, rating));
          const r2 = await saveP;
          if (!r2 || r2.error) return false;
          if (r2.row) { existing = r2.row; _celebrationSaved = true; }
          _goNextOrBack();
          return true;
        }
        // Saved already → only UPDATE the same row when the picked rating changes it.
        if (rating != null && existing?.id && rating !== (existing.child_rating ?? null)) {
          const r3 = await ctx.onSave({ ...base, childRating: rating, existingCompletionId: existing.id, previousStatus: existing.status });
          if (!r3 || r3.error) return false;
          if (r3.row) existing = r3.row;
        }
        _goNextOrBack();
        return true;
      },
      // Wait for the celebration-time save so the sheet UPDATEs the saved row (an INSERT would collide
      // with it and drop the parent's note/photo).
      onParentAdd: (rating) => { if (celCtl) celCtl.close(); saveP.then(() => _openSheetFromCel(result, rating)); },
    });
  }

  // "Parent: add a note or photo" from the celebration → the sheet, prefilled + rating carried;
  // sheet Save replaces celebration-Next (advances to the next open slot / origin).
  function _openSheetFromCel(result, rating) {
    openParentSheet({
      homework: hw, exercise: ex, slot: ctx.slot, scheduledDate: dateStr,
      // After the celebration-time save, `existing` IS the saved row (values already include `result`),
      // so pass no mechanicResult — the sheet would otherwise add it on top again (double count).
      presetStatus: 'done', mechanicResult: _celebrationSaved ? null : result, presetRating: rating, existingCompletion: existing,
      childName: (H.DB?.children?.find(c => c.id === ctx.childId)?.name) || '', firstName, H,
      onSave: (p) => ctx.onSave(p).then(res => { if (!(res && res.error)) _goNextOrBack(); return res; }),
    });
  }

  // Direct sheet (Didn't-do-it / editing a done row) — save returns to the origin screen (unchanged).
  function _openSheetDirect(presetStatus) {
    openParentSheet({
      homework: hw, exercise: ex, slot: ctx.slot, scheduledDate: dateStr,
      presetStatus, mechanicResult: null, existingCompletion: existing,
      childName: (H.DB?.children?.find(c => c.id === ctx.childId)?.name) || '', firstName, H,
      onSave: (p) => ctx.onSave(p).then(res => { if (!(res && res.error)) ctx.onBack(); return res; }),
    });
  }
}
